import { useModalData } from "@circle-react-uikit/ModalV2";
import { ALL_COMMENTS_ANCHOR } from "@circle-react/components/CommentsV3/constants";
import { useProfileContext } from "@circle-react/components/ProfileModalV3/context";
import { isPostDetailsPage } from "@circle-react/helpers/isPostDetailsPage";
import { isSharedPostView } from "@circle-react/helpers/postHelpers";
import { postsPath } from "@circle-react/helpers/urlHelpers";
import { useIsIframeEmbed } from "@circle-react/hooks/useIsIframeEmbed";
import type { Event } from "@circle-react/types/Event";
import { useIsMobile } from "@circleco/compass/hooks/useIsMobile";
import { Link, useMatchRoute } from "@tanstack/react-router";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { usePostModalParams } from "@/react/hooks/usePostModalParams";
import type { Post } from "@/react/types/Post";

import { usePostNavigationContext } from "../PostViewList/PostNavigationContext";

const POST_PAGE_PATH = "/c/$spaceSlug/$postSlug" as const;

interface ShowPageLinkProps extends ComponentPropsWithoutRef<"a"> {
  allComments?: boolean;
  ariaLabel?: string;
  children?: ReactNode;
  forwardedRef?: any;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  post: Post | Event;
  shouldOpenPostModal?: boolean;
}

export const ShowPageLink = ({
  post,
  ariaLabel,
  allComments = false,
  forwardedRef,
  onClick,
  children,
  shouldOpenPostModal = false,
  ...passThroughProps
}: ShowPageLinkProps) => {
  const profileContext = useProfileContext();
  const profileContextMember = profileContext.member;
  const isWithinProfileContext = !!profileContextMember;

  const { postSlug: postSlugInUrl } = usePostModalParams();
  const isIframeEmbed = useIsIframeEmbed();
  const { isInsideModal, onClose } = useModalData();
  const { params, hasNextPage } = usePostNavigationContext();

  const matchRoute = useMatchRoute();
  const isFromEventsDirectory = Boolean(
    matchRoute({ to: "/events", fuzzy: true }),
  );

  const isMobile = useIsMobile();
  const shouldShowPostModal = !isPostDetailsPage() && !isMobile;

  const postPageParams = {
    spaceSlug: post.space_slug,
    postSlug: post.slug,
  };

  const postHref = postsPath({
    spaceSlug: post.space_slug,
    slug: post.slug,
    hash: allComments ? ALL_COMMENTS_ANCHOR : "",
  });

  if (isIframeEmbed) {
    return (
      <a
        href={postHref}
        target="_blank"
        rel="noreferrer"
        aria-label={ariaLabel}
        {...passThroughProps}
        {...(forwardedRef && { ref: forwardedRef })}
      >
        {children}
      </a>
    );
  }

  if (isSharedPostView(post, { postSlugInUrl })) {
    // Using this to do a hard reload to handle authorization on the backend for now
    return (
      <a
        href={postHref}
        aria-label={ariaLabel}
        {...passThroughProps}
        {...(forwardedRef && { ref: forwardedRef })}
      >
        {children}
      </a>
    );
  }

  if (shouldOpenPostModal && shouldShowPostModal) {
    return (
      <Link
        to="."
        unsafeRelative="path"
        search
        state={{
          postModal: postPageParams,
          params,
          hasNextPage,
          isWithinProfileContext,
          profileContextMember,
          shouldFocusOnCommentsSection: allComments,
        }}
        mask={{
          to: POST_PAGE_PATH,
          params: postPageParams,
          unmaskOnReload: true,
        }}
        aria-label={ariaLabel}
        {...passThroughProps}
        {...(forwardedRef && { ref: forwardedRef })}
      >
        {children}
      </Link>
    );
  }

  return (
    <Link
      to={POST_PAGE_PATH}
      params={postPageParams}
      search
      hash={allComments ? ALL_COMMENTS_ANCHOR.replace(/^#/, "") : undefined}
      state={
        isFromEventsDirectory
          ? { from: { path: "/events", query: window.location.search } }
          : undefined
      }
      aria-label={ariaLabel}
      {...passThroughProps}
      {...(forwardedRef && { ref: forwardedRef })}
      onClick={isInsideModal ? onClose : onClick}
    >
      {children}
    </Link>
  );
};
